import styled, { css } from 'styled-components';
import Button from '../../../components/Atoms/Button';
import Separator from '../../../components/Atoms/Separator';
import { tokens, media } from '../../../design/tokens';

interface AnnotationsContainerProps {
  hasNotes: boolean;
}

/*
 * Altura descontada do header em vez de chutada.
 *
 * Era `height: 88vh` com `margin-top: 20px`: nesta rota o header e estatico e
 * ja ocupa 88px no fluxo, entao 88vh + 88px + 20px passava de 100vh e a tela
 * rolava. Junto saiu o `@media (max-height: 580px) { margin-top: 110px }`, um
 * numero magico que empurrava a tela para baixo em janela baixa — exatamente
 * o caso em que ela ja nao cabia.
 *
 * O breakpoint de empilhamento passa de 768px para 980px, que e o valor
 * documentado em DESIGN.md ("A Regra do Overlay Movel") e o mesmo que o JS
 * destas telas ja usava para esconder o player. Entre 768 e 979 o CSS dizia
 * "linha" enquanto o JS ja tinha escondido o video: sobrava o menu lateral ao
 * lado de um vazio.
 */
export const Container = styled.div`
  display: flex;
  align-items: stretch;

  height: calc(100vh - ${tokens.layout.headerHeight});

  ${media.upTo(tokens.breakpoint.md)} {
    flex-direction: column;
    align-items: stretch;

    height: auto;
    max-width: 100%;
    overflow-x: hidden;
  }
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
  height: 100%;
  /* Sem isto o filho com overflow nao encolhe dentro da coluna flex. */
  min-height: 0;

  padding: 0 ${tokens.space.md};
`;

export const VideoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 100%;
  /* Ocupa o que sobra da coluna, em vez de disputar 100% da altura com a
     faixa de anotacoes logo abaixo. */
  flex: 1;
  min-height: 0;

  padding: ${tokens.space.xl};

  svg.close_video {
    display: none;
  }

  ${media.upTo(tokens.breakpoint.md)} {
    padding: 0;
    display: none;
    flex: 0 0 auto;
    height: 0;

    svg.close_video {
      z-index: 7;
      display: block;

      color: ${tokens.color.textWhite};
      fill: ${tokens.color.textWhite};
      font-size: 2rem;

      position: fixed;
      top: 1rem;
      right: 1rem;
    }

    div[from='course'] {
      height: 80%;
    }
  }
`;

export const OverlayVideoModal = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100%;

  margin: 0 auto;

  ${media.upTo(tokens.breakpoint.md)} {
    background-color: ${tokens.color.stageBlack};

    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    z-index: 6;
    padding: 0.5rem;
  }
`;

/*
 * A faixa reservava 30% a 40% da altura da tela mesmo sem nenhuma anotacao —
 * era a origem do vazio grande sob o video. Agora ela ocupa o que o conteudo
 * pede, e so ganha teto (com rolagem interna) quando ha anotacoes de fato.
 */
export const AnnotationsContainer = styled.div<AnnotationsContainerProps>`
  display: flex;
  flex-direction: column;

  width: 100%;
  flex: 0 0 auto;
  min-height: 0;

  padding-bottom: ${tokens.space.md};

  ${props =>
    props.hasNotes &&
    css`
      max-height: 40%;
    `}

  &::-webkit-scrollbar-thumb {
    background: ${tokens.color.marqueeGold} !important;
  }

  h2 {
    margin-left: 1rem;
    color: ${tokens.color.textWhite};
  }

  h3.support_material {
    margin-left: 1rem;
    margin-top: 1rem;
    color: ${tokens.color.textWhite};

    a {
      margin-left: 0.5rem;
      color: ${tokens.color.marqueeGold};
    }
  }

  ${media.upTo(tokens.breakpoint.md)} {
    display: none;
  }
`;

export const AttachmentsList = styled.div`
  margin-left: 1rem;
  margin-top: 1rem;

  h3 {
    color: #fff;
    margin-bottom: 0.5rem;
  }
`;

export const AttachmentRow = styled.div`
  margin-bottom: 0.4rem;

  a {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: rgb(254, 212, 74);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const FileSizeLabel = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
`;

/*
 * Estava absoluto com margin-top negativa, flutuando para fora da faixa e por
 * cima do video. Agora em fluxo.
 *
 * A esquerda, e nao a direita: alinhado ao "Material de Apoio" logo acima (a
 * mesma margem de 1rem), e fora do canto inferior direito, onde o botao
 * flutuante do WhatsApp e fixo na viewport e cobria o rotulo.
 */
export const AddNoteWrapper = styled.div`
  align-self: flex-start;
  margin: ${tokens.space.md} 0 ${tokens.space.md} 1rem;
`;

/*
 * Era 160x30px fixos com o rotulo "Adicionar Anotacao" em 12px: o texto nao
 * cabia e quebrava em duas linhas dentro de uma caixa de 30px de altura.
 * Largura pelo conteudo, altura de alvo de toque, e o rotulo em uma linha.
 */
export const StyledButton = styled(Button)`
  width: auto;
  height: auto;
  min-height: 36px;
  padding: 0 ${tokens.space.lg};

  font-size: ${tokens.font.caption.size};
  white-space: nowrap;
`;

export const NotesWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
  /* overflow: scroll mostrava barra mesmo sem conteudo a rolar. */
  overflow-y: auto;
  min-height: 0;

  /* Eram 128px de padding lateral fixo, herdados de uma coluna bem mais
     larga: numa tela media as anotacoes ficavam espremidas no meio. */
  padding: 0 ${tokens.space.xs};

  &::-webkit-scrollbar-thumb {
    background: ${tokens.color.marqueeGold} !important;
  }
`;

export const SaveNoteButton = styled(Button)`
  width: 180px;
  height: 40px;

  display: flex;
  align-self: flex-end;
  justify-content: center;
  align-items: center;

  font-family: 'Raleway';
  font-size: 12px;
  font-weight: normal;
`;

export const CloseModalButton = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  font-weight: bold;

  margin-top: 8px;
  margin-right: 8px;

  img {
    opacity: 1;
    width: 20px;

    transition: 0.4s;
  }

  &:hover {
    transform: scaleX(1.04) scaleY(1.04);

    img {
      opacity: 0.5;
      cursor: pointer;
    }
  }
`;

export const StyledSeparator = styled(Separator)`
  height: 100% !important;

  ${media.upTo(tokens.breakpoint.md)} {
    display: none;
  }
`;

export const AudioContainer = styled.div`
  width: 100%;
  img {
    margin-bottom: ${tokens.space.xl};
    border-radius: ${tokens.radius.sm};
  }
`;

export const WrapperAudio = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: ${tokens.radius.sm};

  /* Eram 90px/150px fixos, que estouravam a coluna em tela media. */
  padding: clamp(${tokens.space.xl}, 6vw, 90px) clamp(${tokens.space.xl}, 10vw, 150px);
`;
